import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Phone, FileText, Home } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { companyInfo } from '../data/companyInfo';
import { logo, social } from '../data/siteMeta';
import { ROUTE_MAP, getNavigation } from '../data/routes';
import { translateUrl } from '../data/slugMapping';
import ThemeToggle from './ThemeToggle';
import WhatsAppIcon from './WhatsAppIcon';

const EXPERIMENTAL_HEADER_IMAGE_NAV = false;
const NAV_THUMB_FALLBACK = '/images/nav/home.webp';
const NAV_THUMB_BY_KEY = {
  home: '/images/nav/home.webp',
  valveMaintenance: '/images/nav/valveMaintenance.webp',
  products: '/images/nav/products.webp',
  services: '/images/nav/services.webp',
  solutions: '/images/nav/solutions.webp',
  applications: '/images/nav/applications.webp',
  industries: '/images/nav/industries.webp',
  about: '/images/nav/about.webp',
  faq: '/images/nav/faq.webp',
  contact: '/images/nav/contact.webp'
};

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { locale, t, setLocale } = useLanguage();

  const routes = ROUTE_MAP[locale];
  const nav = getNavigation(locale);
  const company = companyInfo?.[locale] || {};

  const primaryPhoneNumber = '+90 216 401 10 50';
  const secondaryPhoneNumber = '+90 539 659 10 50';
  const phoneHref = `tel:${primaryPhoneNumber.replace(/\s/g, '')}`;
  const whatsappHref = 'https://api.whatsapp.com/send?phone=905396591050';

  const homeLabel = t('Ana Sayfa', 'Home', 'Ana Səhifə');
  const handleLanguageChange = (newLocale) => {
    if (newLocale === locale) return;

    const currentPath = location.pathname;
    const newPath = translateUrl(currentPath, newLocale);

    setLocale(newLocale);

    if (newPath !== currentPath) {
      navigate(newPath);
    }
  };

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white dark:bg-gray-900 shadow-md' : 'bg-white dark:bg-gray-900'}`}>
        {/* Top bar */}
        <div className="bg-gray-100 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center py-2 text-sm gap-3">
              <div className="flex items-center space-x-4">
                <a href={social?.linkedin || '#'} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-300 hover:text-[#0077b5] transition-colors">
                  <span className="sr-only">{t('LinkedIn sayfamız', 'Our LinkedIn page', 'LinkedIn səhifəmiz')}</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                </a>
                <a href={social?.youtube || '#'} target="_blank" rel="noopener noreferrer" className="text-gray-600 dark:text-gray-300 hover:text-red-600 transition-colors">
                  <span className="sr-only">{t('YouTube kanalımız', 'Our YouTube channel', 'YouTube kanalımız')}</span>
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
                </a>
                <ThemeToggle />
                <div className="flex items-center gap-1 rounded bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 p-0.5 text-xs font-semibold">
                  {[
                    { code: 'tr', label: 'TR' },
                    { code: 'en', label: 'EN' },
                    { code: 'az', label: 'AZ' }
                  ].map((item) => (
                    <button
                      key={item.code}
                      type="button"
                      onClick={() => handleLanguageChange(item.code)}
                      aria-pressed={locale === item.code}
                      data-testid={`lang-${item.code}`}
                      className={`px-2 py-1 rounded transition-all ${
                        locale === item.code
                          ? 'bg-[#213A8F] text-white'
                          : 'text-gray-600 dark:text-gray-300 hover:bg-[#213A8F] hover:text-white'
                      }`}
                    >
                      {item.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex items-center gap-4">
                <div className="hidden md:flex items-center gap-2">
                  <a
                    href={phoneHref}
                    aria-label={`${t('Hemen ara', 'Call now', 'İndi zəng et')}: ${primaryPhoneNumber}`}
                    title={`${t('Hemen ara', 'Call now', 'İndi zəng et')}: ${primaryPhoneNumber}`}
                    className="interactive-lift inline-flex items-center justify-center h-8 w-8 rounded-full border border-[#213A8F]/25 text-[#213A8F] hover:bg-[#213A8F]/10 transition-colors"
                  >
                    <Phone className="w-4 h-4" />
                  </a>
                  <a
                    href={whatsappHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${t('WhatsApp mesaj gönder', 'Send WhatsApp message', 'WhatsApp mesaj göndər')}: ${secondaryPhoneNumber}`}
                    title={`${t('WhatsApp mesaj gönder', 'Send WhatsApp message', 'WhatsApp mesaj göndər')}: ${secondaryPhoneNumber}`}
                    className="interactive-lift inline-flex items-center justify-center h-8 w-8 rounded-full border border-emerald-300 text-emerald-600 hover:bg-emerald-50 transition-colors"
                  >
                    <WhatsAppIcon className="w-4 h-4" />
                  </a>
                </div>

                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={t('WhatsApp ile iletişime geçin', 'Contact via WhatsApp', 'WhatsApp ilə əlaqə saxlayın')}
                  className="md:hidden inline-flex items-center justify-center h-8 w-8 rounded-full border border-emerald-200 text-emerald-600 hover:bg-emerald-50 transition-colors"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Main navigation */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className={`flex justify-between items-center ${EXPERIMENTAL_HEADER_IMAGE_NAV ? 'py-2.5' : 'py-3'}`}>
            <Link to={routes.home} className="flex flex-col items-center">
              <img
                src={logo || '/images/brand/logo.webp'}
                alt={company?.name || 'Demart'}
                className="h-12 w-auto object-contain dark:brightness-110"
                width={220}
                height={68}
                onError={(e) => { e.target.src = '/images/brand/logo.webp'; }}
              />
              <span className="text-[10px] text-gray-500 dark:text-gray-400 italic mt-0.5 hidden md:block text-center">
                {company?.tagline || t('The Art of Design Engineering & Maintenance', 'The Art of Design Engineering & Maintenance', 'The Art of Design Engineering & Maintenance')}
              </span>
            </Link>

            {EXPERIMENTAL_HEADER_IMAGE_NAV ? (
              <nav className="hidden lg:flex items-end gap-0.5 xl:gap-1">
                {nav.map((item) => {
                  const isActive = location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
                  const thumbSrc = NAV_THUMB_BY_KEY[item.key] || NAV_THUMB_FALLBACK;

                  return (
                    <Link
                      key={item.key}
                      to={item.href}
                      className={`group w-[72px] xl:w-[80px] px-1 py-1 rounded-md transition-all ${
                        isActive
                          ? 'bg-[#213A8F]/10 text-[#213A8F]'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800'
                      }`}
                    >
                      <span className="mx-auto flex h-6 w-11 items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-900 shadow-sm">
                        <img
                          src={thumbSrc}
                          alt={item.name}
                          width={44}
                          height={24}
                          loading="eager"
                          decoding="async"
                          className="h-full w-full object-cover"
                          onError={(event) => {
                            event.currentTarget.src = NAV_THUMB_FALLBACK;
                          }}
                        />
                      </span>
                      <span className="mt-1 block h-[2.05rem] overflow-hidden text-center text-[10px] xl:text-[11px] leading-[1rem] font-semibold">
                        {item.key === 'home' ? homeLabel : item.name}
                      </span>
                    </Link>
                  );
                })}
              </nav>
            ) : (
              <nav className="hidden lg:flex items-center space-x-1">
                <Link
                  to={routes.home}
                  className={`px-2.5 py-2 text-sm font-medium transition-colors whitespace-nowrap flex items-center ${
                    location.pathname === routes.home
                      ? 'text-[#213A8F]'
                      : 'text-gray-700 dark:text-gray-300 hover:text-[#213A8F]'
                  }`}
                >
                  <Home className="w-4 h-4 mr-1" />
                  {homeLabel}
                </Link>
                {nav.filter((item) => item.key !== 'home').map((item) => (
                  <Link
                    key={item.key}
                    to={item.href}
                    className={`px-2.5 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                      location.pathname === item.href
                        ? 'text-[#213A8F]'
                        : 'text-gray-700 dark:text-gray-300 hover:text-[#213A8F]'
                    }`}
                  >
                    {item.name}
                  </Link>
                ))}
              </nav>
            )}

            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className="lg:hidden p-2 text-gray-600 dark:text-gray-300 hover:text-[#213A8F]"
              data-testid="mobile-menu-btn"
              aria-label={isMenuOpen ? t('Menüyü kapat', 'Close menu', 'Menyunu bağla') : t('Menüyü aç', 'Open menu', 'Menyunu aç')}
            >
              {isMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

        </div>

        {/* Mobile menu */}
        {isMenuOpen && (
          <div className="lg:hidden bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-lg">
            <nav className="max-w-7xl mx-auto px-4 py-4 space-y-1">
              <Link
                to={routes.home}
                onClick={() => setIsMenuOpen(false)}
                className={`flex items-center px-4 py-3 rounded-lg transition-colors ${
                  location.pathname === routes.home
                    ? 'bg-[#213A8F] text-white'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
              >
                <Home className="w-4 h-4 mr-2" />
                {homeLabel}
              </Link>
              {nav.filter((item) => item.key !== 'home').map((item) => (
                <Link
                  key={item.key}
                  to={item.href}
                  onClick={() => setIsMenuOpen(false)}
                  className={`block px-4 py-3 rounded-lg transition-colors ${
                    location.pathname === item.href
                      ? 'bg-[#213A8F] text-white'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};

export default Header;
